import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/api";
import { useLang } from "@/i18n";
import CoverScreen from "@/components/CoverScreen";
import InvitationCard from "@/components/InvitationCard";
import CountdownTimer from "@/components/CountdownTimer";
import MapCard from "@/components/MapCard";
import MessageSection from "@/components/MessageSection";
import RSVPForm from "@/components/RSVPForm";
import PhotoUpload from "@/components/PhotoUpload";
import Footer from "@/components/Footer";
import LanguageToggle from "@/components/LanguageToggle";
import { applyTheme } from "@/themes/contract";
import { getTheme } from "@/themes/registry";
import { Loader2 } from "lucide-react";

export default function InvitePage() {
    const { weddingSlug, guestSlug } = useParams();
    const { dir } = useLang();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [opened, setOpened] = useState(false);
    const invitationRef = useRef(null);
    const recordedOpens = useRef(new Set());
    const guestId = data?.guest?.id ?? null;
    const isOpenInvitation = !!data?.guest?.is_open_invitation;

    useEffect(() => {
        setData(null);
        setError(null);
        setOpened(false);
        api.get(`/w/${weddingSlug}/invite/${guestSlug}`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Not found"));
    }, [weddingSlug, guestSlug]);

    useEffect(() => {
        const groom = data?.wedding?.groom_en;
        const bride = data?.wedding?.bride_en;
        if (!groom && !bride) return undefined;
        const previous = document.title;
        document.title = `${groom || ""} & ${bride || ""}`.trim();
        return () => {
            document.title = previous;
        };
    }, [data]);

    useEffect(() => {
        if (!guestId || isOpenInvitation) return;
        const key = `wd-opened:${guestSlug}`;
        if (recordedOpens.current.has(key)) return;
        try {
            if (sessionStorage.getItem(key)) {
                recordedOpens.current.add(key);
                return;
            }
            sessionStorage.setItem(key, "1");
        } catch {
            // sessionStorage can be unavailable; the ref still limits this mount to one POST.
        }
        recordedOpens.current.add(key);
        api.post(`/w/${weddingSlug}/invite/${guestSlug}/opened`).catch(() => {});
    }, [weddingSlug, guestSlug, guestId, isOpenInvitation]);

    const handleOpen = () => {
        setOpened(true);
        setTimeout(() => {
            invitationRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    const theme = getTheme(data?.wedding?.theme_id);
    const themeProps = {
        className: "wd-theme",
        "data-ornament": theme.ornament,
        style: applyTheme(theme),
    };

    if (error) {
        return (
            <div {...themeProps} className="wd-theme min-h-[100dvh] flex items-center justify-center p-6 wd-paper-texture">
                <div className="wd-card p-10 text-center max-w-md">
                    <p className="wd-heading text-3xl text-[var(--wd-primary)]">404</p>
                    <p className="mt-3 text-[var(--wd-text-muted)]" data-testid="invite-not-found">
                        {error}
                    </p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div {...themeProps} className="wd-theme min-h-[100dvh] flex items-center justify-center wd-paper-texture">
                <Loader2 className="animate-spin text-[var(--wd-primary)]" size={32} data-testid="invite-loading" />
            </div>
        );
    }

    const { wedding, guest, rsvp } = data;

    return (
        <div {...themeProps} dir={dir} className="wd-theme wd-paper-texture min-h-[100dvh]">
            <div className="fixed top-4 right-4 z-30" style={{ [dir === "rtl" ? "left" : "right"]: "1rem", [dir === "rtl" ? "right" : "left"]: "auto" }}>
                <LanguageToggle />
            </div>

            <CoverScreen
                wedding={wedding}
                guestName={guest.name}
                englishGuestName={guest.english_name}
                onOpen={handleOpen}
            />

            {opened && (
                <div ref={invitationRef} className="wd-fade-in">
                    <InvitationCard wedding={wedding} guestName={guest.name} />
                    <RSVPForm
                        slug={guestSlug}
                        guestName={guest.name}
                        initialRsvp={rsvp}
                        requireNameForAttending={isOpenInvitation}
                        lockName={!isOpenInvitation}
                    />
                    <CountdownTimer dateIso={wedding.date_iso} />
                    <MapCard wedding={wedding} />
                    <MessageSection slug={guestSlug} requireName={isOpenInvitation} />
                    <PhotoUpload slug={guestSlug} guestName={guest.name} />
                    <Footer wedding={wedding} />
                </div>
            )}
        </div>
    );
}
